import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Zap, Clock, DollarSign, MapPin, Building2, CheckCircle, XCircle, ArrowRight, Timer, TrendingUp } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const InstantOffersPage = () => {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({ pending: 0, accepted: 0, avg_match: 0, avg_salary: '$0' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOffers(); }, [user]);

  const loadOffers = async () => {
    try {
      const data = await api.getInstantOffers();
      setOffers(data.offers || []);
      setStats(data.stats || stats);
    } catch {
      setOffers([
        { offer_id: '1', title: 'Senior ML Engineer', company: 'TechCorp AI', company_logo: 'https://ui-avatars.com/api/?name=TechCorp+AI&background=6366F1&color=fff', salary: '$180,000/yr', hourly_rate: '$95/hr', location: 'Remote', offer_type: 'Full-time', description: 'Join our ML team. Immediate start, no interview required.', expires_in: '23 hours', urgent: true, benefits: ['Health Insurance', 'Equity', '401k Match', 'Unlimited PTO'], match_score: 97, status: 'pending' },
        { offer_id: '2', title: 'Backend Developer', company: 'DataMinds', company_logo: 'https://ui-avatars.com/api/?name=DataMinds&background=8B5CF6&color=fff', salary: '$150,000/yr', hourly_rate: '$80/hr', location: 'Remote', offer_type: 'Contract', description: 'Build scalable backend systems.', expires_in: '2 days', urgent: false, benefits: ['Flexible Hours', 'Equipment Budget'], match_score: 92, status: 'pending' },
        { offer_id: '3', title: 'Full Stack Engineer', company: 'StartupXYZ', company_logo: 'https://ui-avatars.com/api/?name=StartupXYZ&background=F59E0B&color=fff', salary: '$160,000/yr', hourly_rate: '$85/hr', location: 'Remote', offer_type: 'Full-time', description: 'Lead product development.', expires_in: 'Accepted', urgent: false, benefits: ['Equity', 'Health Insurance'], match_score: 90, status: 'accepted' },
      ]);
      setStats({ pending: 2, accepted: 1, avg_match: 93, avg_salary: '$165k' });
    } finally { setLoading(false); }
  };

  const handleRespond = async (offerId, action) => {
    try { await api.respondToOffer(offerId, action); } catch {}
    setOffers(offers.map(o => o.offer_id === offerId ? { ...o, status: action === 'accept' ? 'accepted' : 'declined' } : o));
    toast({ title: action === 'accept' ? 'Offer accepted!' : 'Offer declined', description: action === 'accept' ? 'Congratulations! Onboarding details will follow.' : "We'll find more matches for you." });
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2"><Zap className="w-8 h-8 text-yellow-500" /><h1 className="text-3xl font-bold text-gray-900">Instant Offers</h1></div>
            <p className="text-gray-600">Pre-approved job offers based on your profile, skills, and assessment scores. No interviews needed!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 text-center"><Zap className="w-8 h-8 text-yellow-500 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.pending}</div><div className="text-sm text-gray-600">Pending Offers</div></Card>
            <Card className="p-6 text-center"><CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.accepted}</div><div className="text-sm text-gray-600">Accepted</div></Card>
            <Card className="p-6 text-center"><TrendingUp className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.avg_match}%</div><div className="text-sm text-gray-600">Match Score Avg</div></Card>
            <Card className="p-6 text-center"><DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.avg_salary}</div><div className="text-sm text-gray-600">Avg Offer Salary</div></Card>
          </div>

          <div className="space-y-6">
            {offers.map(offer => (
              <Card key={offer.offer_id} className={`p-6 ${offer.urgent && offer.status === 'pending' ? 'border-2 border-orange-300 ring-1 ring-orange-200' : ''} ${offer.status === 'expired' ? 'opacity-60' : ''}`}>
                {offer.urgent && offer.status === 'pending' && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-orange-50 rounded-lg">
                    <Timer className="w-4 h-4 text-orange-600" /><span className="text-sm font-medium text-orange-800">Urgent: Expires in {offer.expires_in}</span>
                  </div>
                )}
                <div className="flex items-start gap-4">
                  <img src={offer.company_logo} alt={offer.company} className="w-16 h-16 rounded-xl" />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-xl font-semibold text-gray-900">{offer.title}</h3>
                      <Badge className={offer.status === 'accepted' ? 'bg-green-100 text-green-800' : offer.status === 'declined' ? 'bg-red-100 text-red-800' : 'bg-[#E0E8D0] text-[#0A3900]'}>
                        {offer.status === 'pending' ? `${offer.match_score}% match` : offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                      </Badge>
                    </div>
                    <p className="text-gray-600 flex items-center gap-1 mb-2"><Building2 className="w-4 h-4" />{offer.company}</p>
                    <p className="text-gray-700 mb-3">{offer.description}</p>
                    <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1 font-semibold text-gray-900"><DollarSign className="w-4 h-4" />{offer.salary}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{offer.location}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{offer.offer_type}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">{offer.benefits.map((b, i) => (<span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">{b}</span>))}</div>
                    {offer.status === 'pending' && (
                      <div className="flex gap-3 pt-4 border-t">
                        <Button onClick={() => handleRespond(offer.offer_id, 'accept')} className="bg-[#0A3900] hover:bg-[#1A5C0A]"><CheckCircle className="w-4 h-4 mr-2" />Accept Offer</Button>
                        <Button variant="outline" onClick={() => handleRespond(offer.offer_id, 'decline')}><XCircle className="w-4 h-4 mr-2" />Decline</Button>
                        <Button variant="ghost">Negotiate <ArrowRight className="w-4 h-4 ml-2" /></Button>
                      </div>
                    )}
                    {offer.status === 'accepted' && (
                      <div className="mt-2 p-3 bg-green-50 rounded-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" /><span className="text-green-800 font-medium">You accepted this offer. Onboarding details have been sent to your email.</span></div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantOffersPage;
